import { useLayoutEffect, useRef } from "react";

const VISUAL_VIEWPORT_HEIGHT = "--visual-viewport-height";

/**
 * Writes `--visual-viewport-height` on `<html>` while mounted.
 * iOS keeps the layout viewport tall when the keyboard opens; fixed overlays need this to shrink.
 */
export function useVisualViewportHeight(): void {
    // Skip redundant writes during iOS scroll pans.
    const writtenHeightRef = useRef<number>();

    useLayoutEffect(() => {
        const viewport = window.visualViewport;
        if (!viewport) {
            return () => {};
        }

        const apply = () => {
            const rounded = Math.round(viewport.height);
            if (rounded === writtenHeightRef.current) {
                return;
            }
            writtenHeightRef.current = rounded;
            document.documentElement.style.setProperty(VISUAL_VIEWPORT_HEIGHT, `${rounded}px`);
        };

        apply();
        // iOS can report height changes via scroll, not just resize.
        viewport.addEventListener("resize", apply);
        viewport.addEventListener("scroll", apply);
        return () => {
            viewport.removeEventListener("resize", apply);
            viewport.removeEventListener("scroll", apply);
        };
        // Nested consumers each attach a listener; writes are idempotent. Variable is not removed on unmount.
    }, []);
}
